import { z } from "zod";
import type { QrExtraPack } from "./site";
import type { WeddingAddonStatus } from "./constants";
import type { WeddingPackageInfo } from "./orders";

/**
 * Misafir kotası (2026-10-15) — organizatör ve admin panelinin ortak "Misafir
 * kotası" kartının veri modeli. Sunucu: `GET …/w/:slug/capacity`.
 *
 * - `limit`: paket misafir limiti = max(hesap planı, ödenmiş sipariş hakkı).
 *   Organizatörün elle eklediği misafirler buna bakar.
 * - `extraSeats`: açık ekstra paketlerin toplamı (`pending` dahil — açınca aktif).
 * - `qrLimit = limit + extraSeats`: QR ile kaydın sınırı.
 */
export interface WeddingAddonRow {
  id: string;
  wedding_id: string;
  kind: "qr_extra";
  seats: number;
  amount_try: number;
  status: WeddingAddonStatus;
  created_by: string;
  cancel_reason: string | null;
  admin_note: string | null;
  created_at: string;
  paid_at: string | null;
  cancelled_at: string | null;
}

export interface GuestCapacity {
  guests: number;
  limit: number;
  extraSeats: number;
  qrLimit: number;
  /** Açılabilecek paketler (admin ayarı); `packsEnabled=false` → yeni paket açılamaz. */
  packs: QrExtraPack[];
  packsEnabled: boolean;
  /** İptal edilmemiş paketler (yeni → eski). */
  addons: WeddingAddonRow[];
  /** Migration 056 uygulanmış mı (yoksa paket açılamaz; kota kuralı yine çalışır). */
  addonsAvailable: boolean;
  /** Düğünün paketi (sipariş ya da hesabın deneme planı) ve geçerli limitleri. */
  package?: WeddingPackageInfo;
  /** Kullanım — paket kartında limitlerin yanında. */
  usage?: { media: number; storageBytes: number; smsSegments: number };
}

/** QR'dan bir kişi daha kaydolabilir mi? */
export function qrHasRoom(c: Pick<GuestCapacity, "guests" | "qrLimit">): boolean {
  return c.guests < c.qrLimit;
}

/** Organizatör paketi geri çekebilir mi: yalnız `pending` ve koltukları kullanılmamışsa. */
/**
 * Arayüz ipucu: paket geri çekilebilir görünür mü. Asıl kural SUNUCUDA: paket açıldıktan sonra
 * QR ile kaydolmuş HİÇ misafir yoksa (silinmişler dahil) — misafirleri silip ücretsiz geri çekme olmasın.
 */
export function canWithdrawAddon(c: Pick<GuestCapacity, "guests" | "qrLimit">, addon: Pick<WeddingAddonRow, "status" | "seats">): boolean {
  return addon.status === "pending" && c.guests <= c.qrLimit - addon.seats;
}

/**
 * Ödenmemiş ek paket bu kadar gün sonra iptal edilir (koltuklar kapanır; kaydolmuş misafir
 * silinmez). Aynı anda düğün başına EN FAZLA bir ödenmemiş paket (2026-10-16 kararı).
 */
export const ADDON_PENDING_DAYS = 7;

export const addonCreateSchema = z.object({
  seats: z.number().int().min(1).max(1000),
  /** Uyarı penceresindeki "fiyatı ve koşulları onaylıyorum". */
  accept: z.literal(true),
  /** Yalnız admin: ikram (0 ₺) ya da özel adet. Organizatörde yok sayılır. */
  comp: z.boolean().optional(),
  /** İkram (0 ₺) gerekçesi — admin ikramında ZORUNLU (sunucu denetler; deftere yazılır). */
  reason: z.string().trim().min(3).max(300).optional(),
});
export type AddonCreate = z.infer<typeof addonCreateSchema>;

export const addonAdminPatchSchema = z
  .object({
    status: z.enum(["paid", "cancelled"]).optional(),
    /** Ödeme referansı / not. Tutarı olan paket "ödendi" işaretlenirken ZORUNLU (sunucu denetler). */
    adminNote: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.status !== undefined || v.adminNote !== undefined, "empty");
export type AddonAdminPatch = z.infer<typeof addonAdminPatchSchema>;
